import React, { useState, useEffect, useRef } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ADMIN_EMAILS } from '../firebase/config';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase/config';
import { 
  FaTruck, 
  FaUserCircle, 
  FaChevronDown,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaTachometerAlt
} from 'react-icons/fa';
import './Navbar.css';

const Navbar = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const dropdownRef = useRef(null);

  const isAdmin = currentUser ? ADMIN_EMAILS.includes(currentUser.email) : false;
  const isHomePage = location.pathname === '/';

  // Charger les informations utilisateur depuis Firebase
  useEffect(() => {
    if (!currentUser) return;

    const userRef = ref(database, `utilisateurs/${currentUser.uid}`);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setUserInfo(data);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { label: 'Accueil', to: 'home' },
    { label: 'Services', to: 'services' },
    { label: 'Pourquoi nous', to: 'why-us' },
    { label: 'Paiement', to: 'payment' },
    { label: 'Avis', to: 'testimonials' },
    { label: 'Contact', to: 'contact' }
  ];

  // Obtenir le nom d'affichage de l'utilisateur
  const getDisplayName = () => {
    if (!currentUser) return '';
    
    if (userInfo?.prenom && userInfo?.nom) {
      return `${userInfo.prenom} ${userInfo.nom}`;
    }
    if (userInfo?.prenom) {
      return userInfo.prenom;
    }
    if (userInfo?.nomComplet) {
      return userInfo.nomComplet;
    }
    return currentUser.email?.split('@')[0] || '';
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error('Erreur lors de la déconnexion', error);
    }
  };

  const dashboardLink = isAdmin ? '/admin/dashboard' : '/dashboard';

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <RouterLink to="/" className="nav-logo">
          <FaTruck className="logo-icon" />
          <span className="logo-text">SMTD</span>
        </RouterLink>

        <div className="nav-menu-desktop">
          {isHomePage ? (
            navItems.map((item) => (
              <ScrollLink
                key={item.to}
                to={item.to}
                spy={true}
                smooth={true}
                offset={-80}
                duration={500}
                activeClass="active"
                className="nav-link"
              >
                {item.label}
              </ScrollLink>
            ))
          ) : (
            <RouterLink to="/" className="nav-link">Accueil</RouterLink>
          )}

          {currentUser ? (
            <div className="user-menu" ref={dropdownRef}>
              <button 
                className="user-menu-btn"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <FaUserCircle className="user-icon" />
                <span className="user-name">
                  {getDisplayName()}
                </span>
                <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="dropdown-menu">
                  <RouterLink 
                    to={dashboardLink} 
                    className="dropdown-item"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <FaTachometerAlt /> {isAdmin ? 'Administration' : 'Mon espace'}
                  </RouterLink>
                  <button onClick={handleLogout} className="dropdown-item logout">
                    <FaSignOutAlt /> Déconnexion
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-buttons">
              <RouterLink to="/login" className="btn-outline-nav">Connexion</RouterLink>
              <RouterLink to="/register" className="btn-primary-nav">Inscription</RouterLink>
            </div>
          )}
        </div>

        <button 
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Menu"
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="mobile-menu">
          {isHomePage ? (
            navItems.map((item) => (
              <ScrollLink
                key={item.to}
                to={item.to}
                spy={true}
                smooth={true}
                offset={-80}
                duration={500}
                className="mobile-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </ScrollLink>
            ))
          ) : (
            <RouterLink to="/" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>
              Accueil
            </RouterLink>
          )}
          
          {currentUser ? (
            <>
              <RouterLink 
                to={dashboardLink} 
                className="mobile-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Mon espace
              </RouterLink>
              <button onClick={handleLogout} className="mobile-link logout">
                Déconnexion
              </button>
            </>
          ) : (
            <>
              <RouterLink to="/login" className="mobile-link">Connexion</RouterLink>
              <RouterLink to="/register" className="mobile-link">Inscription</RouterLink>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;